import { __awaiter, __generator } from "tslib";
import { easel, images, tokenProvider, withActiveToken } from './api';
import { mimeTypeExtensions } from './constants';
var Canvas = /** @class */ (function () {
    function Canvas(opts) {
        if (opts === void 0) { opts = {}; }
        if (!opts.id) {
            throw new Error('No id supplied to Canvas constructor');
        }
        this.id = opts.id;
        this.projectId = opts.projectId;
        this.token = opts.token;
        this.userId = opts.userId;
        this._saved = true;
        this._refreshToken = this._refreshToken.bind(this);
    }
    Canvas.new = function (projectId) {
        // Interacts with Marvel to reserve a canvas id
        // and then returns a promise resolving to a
        // Canvas object
        return tokenProvider
            .newCanvas(projectId)
            .then(function (response) {
            return {
                id: response.body.canvas_id,
                projectId: projectId,
                token: {
                    token: response.body.token,
                    expires: new Date(Date.parse(response.body.exp)),
                },
                userId: response.body.marvel_user_id,
            };
        })
            .then(function (data) {
            var c = new Canvas(data);
            c._saved = false;
            return c;
        });
    };
    Canvas.newComponent = function () {
        // Interacts with Marvel to reserve a canvas id and then returns a
        // promise resolving to a Canvas object
        return tokenProvider
            .newComponent()
            .then(function (response) { return ({
            id: response.body.canvas_id,
            token: {
                token: response.body.token,
                expires: new Date(Date.parse(response.body.exp)),
            },
            userId: response.body.marvel_user_id,
        }); })
            .then(function (data) {
            var c = new Canvas(data);
            c._saved = false;
            return c;
        });
    };
    Canvas.newFromContent = function (projectId, contentId) {
        // Interacts with Marvel to reserve a canvas id
        // and then returns a promise resolving to a
        // Canvas object
        return tokenProvider
            .newCanvasForContent(projectId, contentId)
            .then(function (response) {
            return tokenProvider.getAuth(response.body.es_canvas_id, projectId);
        })
            .then(function (response) {
            return {
                id: response.body.canvas_id,
                projectId: projectId,
                token: {
                    token: response.body.token,
                    expires: new Date(Date.parse(response.body.exp)),
                },
                userId: response.body.marvel_user_id,
            };
        })
            .then(function (data) {
            var c = new Canvas(data);
            c._saved = false;
            return c;
        });
    };
    Canvas.load = function (projectId, id) {
        return tokenProvider
            .getAuth(id, projectId)
            .then(function (response) {
            return {
                id: response.body.canvas_id,
                projectId: projectId,
                token: {
                    token: response.body.token,
                    expires: new Date(Date.parse(response.body.exp)),
                },
                userId: response.body.marvel_user_id,
            };
        })
            .then(function (data) {
            return new Canvas(data);
        });
    };
    Canvas.prototype._refreshToken = function () {
        var _this = this;
        return tokenProvider
            .getAuth(this.id, this.projectId)
            .then(function (response) {
            return {
                token: response.body.token,
                expires: new Date(Date.parse(response.body.exp)),
            };
        })
            .then(function (token) {
            _this.token = token;
            return token;
        });
    };
    Canvas.prototype._withActiveToken = function (fn) {
        return withActiveToken(fn, this.token, this._refreshToken);
    };
    Canvas.prototype._create = function (data) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, , 2, 3]);
                        return [4 /*yield*/, this._withActiveToken(function (_a) {
                                var token = _a.token;
                                return easel.create({
                                    data: data,
                                    token: token,
                                    userId: _this.userId,
                                });
                            })];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        this._saved = true;
                        return [7 /*endfinally*/];
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    Canvas.prototype._save = function (data) {
        var _this = this;
        return this._withActiveToken(function (_a) {
            var token = _a.token;
            return easel.save({
                data: data,
                id: _this.id,
                token: token,
                userId: _this.userId,
            });
        });
    };
    Canvas.prototype.store = function (data) {
        if (data.canvas.id !== this.id) {
            // just a sanity check
            throw new Error("Canvas.id does not match data.id, data.canvas.id: ".concat(data.canvas.id, ", this.id: ").concat(this.id));
        }
        if (!this._saved) {
            return this._create(data);
        }
        return this._save(data);
    };
    Canvas.prototype.fetch = function () {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._withActiveToken(function (_a) {
                            var token = _a.token;
                            return easel.fetch({
                                id: _this.id,
                                token: token,
                                userId: _this.userId,
                            });
                        })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.body];
                }
            });
        });
    };
    Canvas.prototype.fetchCode = function (language, colorMode) {
        return __awaiter(this, void 0, void 0, function () {
            var response;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this._withActiveToken(function (_a) {
                            var token = _a.token;
                            return easel.fetchCode({
                                id: _this.id,
                                token: token,
                                type: language,
                                userId: _this.userId,
                                colorMode: colorMode,
                            });
                        })];
                    case 1:
                        response = _a.sent();
                        return [2 /*return*/, response.body];
                }
            });
        });
    };
    Canvas.prototype.delete = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                if (!this._saved) {
                    throw new Error('Can not delete a canvas that has not yet been saved');
                }
                return [2 /*return*/, this._withActiveToken(function (_a) {
                        var token = _a.token;
                        return easel.delete({
                            id: _this.id,
                            token: token,
                            userId: _this.userId,
                        });
                    })];
            });
        });
    };
    return Canvas;
}());
export default Canvas;
export var canvasUtils = {
    uploadDataUri: function (dataUri, uuid, imageParams, name) {
        if (uuid === void 0) { uuid = null; }
        if (imageParams === void 0) { imageParams = {}; }
        if (name === void 0) { name = null; }
        return __awaiter(this, void 0, void 0, function () {
            var parts, type, byteString, aBuffer, intArray, i, filename, extension, blob, file;
            return __generator(this, function (_a) {
                if (dataUri.match(/^http/)) {
                    // It's HTTP!
                    return [2 /*return*/, images.uploadFromURL(dataUri, uuid, imageParams)];
                }
                parts = dataUri.split(',');
                type = parts[0].split(':')[1].split(';')[0];
                byteString = atob(parts[1]);
                aBuffer = new ArrayBuffer(byteString.length);
                intArray = new Uint8Array(aBuffer);
                for (i = 0; i < byteString.length; i++) {
                    intArray[i] = byteString.charCodeAt(i);
                }
                filename = name;
                if (!name) {
                    extension = mimeTypeExtensions[type];
                    filename = extension ? "image.".concat(extension) : 'blob';
                }
                blob = new Blob([intArray], {
                    type: type,
                    name: filename,
                });
                try {
                    file = new File([blob], filename, {
                        type: blob.type,
                    });
                }
                catch (err) {
                    // IE and Edge don't support the new file constructor
                    file = blob;
                }
                return [2 /*return*/, images.upload(file, uuid)];
            });
        });
    },
};
